import { Suspense, lazy, useState, type ReactElement, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { standInTrigger, useIdlePreload } from '../../utils/deferred';

export interface MenuItem {
  id: string;
  label: ReactNode;
  icon?: LucideIcon;
  onSelect?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  /** Draw a divider above this item. */
  separated?: boolean;
}

export interface MenuProps {
  /** The element that opens the menu; usually a Button or IconButton. */
  trigger: ReactElement;
  items: MenuItem[];
  label?: string;
  align?: 'start' | 'center' | 'end';
  defaultOpen?: boolean;
}

const load = () => import('./MenuPopup');
const MenuPopup = lazy(load);

export function Menu(props: MenuProps) {
  const [opened, setOpened] = useState(false);
  useIdlePreload(load);
  const standIn = standInTrigger(
    props.trigger,
    load,
    () => {
      setOpened(true);
    },
    'menu',
  );
  if (!opened && !props.defaultOpen) return standIn;
  return (
    <Suspense fallback={standIn}>
      <MenuPopup {...props} defaultOpen={props.defaultOpen ?? opened} />
    </Suspense>
  );
}
